"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireDefault(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _classnames = _interopRequireDefault(require("classnames"));

var _hoverintent = _interopRequireDefault(require("hoverintent"));

var _popover = _interopRequireDefault(require("../popover"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

const TRIGGER_HOVER = 'hover';
const TRIGGER_FOCUS = 'focus';
const TRIGGER_CLICK = 'click';
/**
 * A smart trigger for managing the state of a [Popover](#popover).
 *
 * If you want to manage the open-closed state of the Popover yourself,
 * use [Popover](#popover) directly.
 *
 * `respondsToClick`, `respondsToHover`, and `respondsToFocus` props determine
 * the means of opening and closing the Popover with interactions. If you open
 * the Popover with a hover or focus, you can also close it by hovering away or
 * blurring. If, however, you open the Popover with a click, you can only close
 * it with another click, on the trigger or outside the Popover. If you
 * open the Popover with a hover and *then* you click, this is equivalent to
 * opening it with a click directly, so it will only close with another
 * click (not from just hovering away).
 */

class PopoverTrigger extends _react.default.Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      visible: false,
      activeTriggerType: null
    });

    _defineProperty(this, "openPopover", (trigger, callback) => {
      const alreadyOpen = !!this.state.visible;
      this.setState({
        visible: true,
        activeTriggerType: trigger
      }, () => {
        if (!alreadyOpen && this.props.onPopoverOpen) {
          this.props.onPopoverOpen();
        }

        if (callback) callback();
      });
    });

    _defineProperty(this, "closePopover", callback => {
      const alreadyClosed = !this.state.visible;
      this.setState({
        visible: false,
        activeTriggerType: null
      }, () => {
        if (!alreadyClosed && this.props.onPopoverClose) {
          this.props.onPopoverClose();
        }

        if (callback) callback();
      });
    });

    _defineProperty(this, "onAnyClick", event => {
      // If the trigger responds to focus but not click and you *first*
      // focus *then* click, that click closes the popover instead of
      // leaving it open even when the mouse moves away.
      if (!this.props.respondsToClick && this.state.visible && this.state.activeTriggerType === TRIGGER_FOCUS) {
        this.hide();
        return;
      }

      if (!this.hoverIsBlocked && (this.props.disabled || !this.props.respondsToClick)) {
        return;
      } // A trigger click always takes precedence over hover or focus interactions.
      // If the popover is already open by hover or focus, a click will change its
      // priorities, so it stays open even if you mouseleave or blur


      if (this.state.visible && this.state.activeTriggerType === TRIGGER_CLICK && (!this.popoverElement || !this.popoverElement.contains(event.target))) {
        this.hide();
      } else {
        this.showBecauseClick();
      }
    });

    _defineProperty(this, "blockHover", () => {
      if (!this.hoverIsBlocked) {
        this.hoverIsBlocked = true;
        this.addMouseMoveUnblockTimeout = setTimeout(() => {
          document.addEventListener('mousemove', this.unblockHover);
        }, 100);
      }
    });

    _defineProperty(this, "unblockHover", () => {
      if (this.hoverIsBlocked) {
        this.hoverIsBlocked = false;
        document.removeEventListener('mousemove', this.unblockHover);
      }
    });

    _defineProperty(this, "onTriggerMouseEnter", () => {
      if (this.props.disabled || !this.props.respondsToHover || this.state.activeTriggerType && this.state.activeTriggerType !== TRIGGER_HOVER || this.hoverIsBlocked) {
        return;
      }

      this.showBecauseHover();
    });

    _defineProperty(this, "onTriggerMouseLeave", event => {
      if (this.props.disabled || !this.props.respondsToHover || this.state.activeTriggerType !== TRIGGER_HOVER || this.hoverIsBlocked) {
        return;
      } // When you hover over the popover, you do not want to disappear from beneath
      // your cursor. This keeps the popover open when your cursor is within it.


      if (!this.popoverElement || !this.popoverElement.contains(event.relatedTarget)) {
        this.hideBecauseHover();
      }
    });

    _defineProperty(this, "onPopoverMouseLeave", event => {
      if (this.props.disabled || !this.props.respondsToHover || this.state.activeTriggerType !== TRIGGER_HOVER || this.hoverIsBlocked) {
        return;
      }

      if (!this.triggerElement || !this.triggerElement.contains(event.relatedTarget)) {
        this.hideBecauseHover();
      }
    });

    _defineProperty(this, "onTriggerFocus", () => {
      if (this.props.disabled || !this.props.respondsToFocus || this.state.visible || this.triggerFocusIsBlocked) {
        return;
      }

      this.showBecauseTriggerFocus();
    });

    _defineProperty(this, "onTriggerBlur", () => {
      if (this.props.disabled || !this.props.respondsToFocus || !this.state.visible || this.state.activeTriggerType !== TRIGGER_FOCUS) {
        return;
      }

      this.hide();
    });

    _defineProperty(this, "onTriggerKeyDown", event => {
      const escapeCloses = this.props.popoverProps.escapeCloses;
      if (escapeCloses === false) return;

      if (event.key === 'Escape') {
        this.hide();
      }
    });

    _defineProperty(this, "showBecauseClick", () => {
      this.clearTimeouts();
      this.removePopoverListeners();
      this.openPopover(TRIGGER_CLICK);
    });

    _defineProperty(this, "showBecauseHover", () => {
      this.openPopover(TRIGGER_HOVER);
    });

    _defineProperty(this, "showBecauseTriggerFocus", () => {
      this.clearTimeouts();
      this.removePopoverListeners();
      this.openPopover(TRIGGER_FOCUS);
    });

    _defineProperty(this, "hideBecauseHover", () => {
      this.hide();
    });

    _defineProperty(this, "hide", () => {
      if (!this.state.visible) return;
      this.clearTimeouts();
      this.removePopoverListeners();
      this.closePopover(); // If focus was inside the popover, after the popover closes focus will
      // return to the trigger. We need to prevent that from re-opening the
      // trigger.

      this.triggerFocusIsBlocked = true;
      this.cancelTriggerFocusIsBlockedTimeout = setTimeout(() => {
        this.triggerFocusIsBlocked = false;
      }, 100);
    });

    _defineProperty(this, "removePopoverListeners", () => {
      const {
        popoverElement
      } = this;
      if (!popoverElement) return;
      popoverElement.removeEventListener('mouseleave', this.onPopoverMouseLeave);
    });

    _defineProperty(this, "ignoreClickWithinElement", element => {
      if (this.triggerElement && this.triggerElement.contains(element)) return true;
      const ignoreClickWithinElement = this.props.popoverProps.ignoreClickWithinElement;

      if (ignoreClickWithinElement) {
        return ignoreClickWithinElement(element);
      }

      return false;
    });

    _defineProperty(this, "getAnchorElement", () => {
      const getAnchorElement = this.props.popoverProps.getAnchorElement;

      if (getAnchorElement) {
        return getAnchorElement();
      }

      return this.triggerElement.firstChild;
    });

    _defineProperty(this, "setTriggerElement", element => {
      this.triggerElement = element;
    });

    _defineProperty(this, "setPopoverElement", element => {
      this.popoverElement = element;
    });

    _defineProperty(this, "getPopoverContent", () => {
      const {
        content
      } = this.props;

      if (typeof content === 'function') {
        return content();
      } else {
        return content;
      }
    });
  }

  componentDidMount() {
    if (!this.triggerElement) return;
    this.triggerElement.addEventListener('touchstart', this.blockHover);
    this.triggerElement.addEventListener('mouseout', this.onTriggerMouseLeave);
    this.hoverListener = (0, _hoverintent.default)(this.triggerElement, this.onTriggerMouseEnter, () => {});
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      popoverElement
    } = this;
    const justOpened = prevState.visible === false && this.state.visible === true;

    if (justOpened && popoverElement && // If the popover was opened by hover, pay attention to hovering
    // within the popover itself
    this.state.activeTriggerType === TRIGGER_HOVER) {
      popoverElement.addEventListener('mouseleave', this.onPopoverMouseLeave);
    }

    if (!prevProps.disabled && this.props.disabled) {
      this.hide();
    }
  }

  componentWillUnmount() {
    this.hoverListener.remove();
    this.triggerElement.removeEventListener('touchstart', this.blockHover);
    document.removeEventListener('mousemove', this.unblockHover);
    this.clearTimeouts();
  }

  clearTimeouts() {
    if (this.cancelTriggerFocusIsBlockedTimeout) clearTimeout(this.cancelTriggerFocusIsBlockedTimeout);
    if (this.addMouseMoveUnblockTimeout) clearTimeout(this.addMouseMoveUnblockTimeout);
  }

  render() {
    const {
      props,
      state
    } = this;
    let popover = null;

    if (state.visible) {
      // Only send focus inside if we've clicked to open
      const receiveFocus = props.receiveFocus && state.activeTriggerType === TRIGGER_CLICK;
      const trapFocus = props.trapFocus && state.activeTriggerType === TRIGGER_CLICK;
      popover = /*#__PURE__*/_react.default.createElement(_popover.default, _extends({
        onExit: this.hide
      }, props.popoverProps, {
        getAnchorElement: this.getAnchorElement,
        ignoreClickWithinElement: this.ignoreClickWithinElement,
        receiveFocus: receiveFocus,
        trapFocus: trapFocus,
        onElement: this.setPopoverElement
      }), this.getPopoverContent());
    } // Tooltips need to the popover content to be present in the DOM
    // with the appropriate id, so aria-labelledby will work


    let hiddenContent = null;

    if (!state.visible && props.renderHiddenContent) {
      hiddenContent = /*#__PURE__*/_react.default.createElement("div", _extends({
        className: "hide-visually"
      }, this.props.popoverProps.passthroughProps || {}), this.getPopoverContent());
    }

    const classes = (0, _classnames.default)({
      block: props.block,
      'inline-block': !props.block
    });
    return /*#__PURE__*/_react.default.createElement("div", _extends({
      ref: this.setTriggerElement,
      className: classes
    }, props.passthroughTriggerProps, {
      onClick: this.onAnyClick,
      onFocus: this.onTriggerFocus,
      onBlur: this.onTriggerBlur,
      onKeyDown: this.onTriggerKeyDown
    }), props.children, hiddenContent, popover);
  }

}

exports.default = PopoverTrigger;
PopoverTrigger.propTypes = {
  /**
   * The trigger content. This can either be a string or valid JSX.
   */
  children: _propTypes.default.node.isRequired,

  /**
   * If `true`, the element will be `block` displayed instead of `inline-block`.
   *
   * This is sometimes necessary to get your pixel-perfect layout.
   */
  block: _propTypes.default.bool,

  /**
   * The popover content. This can either be a string, valid JSX, or a function
   * returning either.
   */
  content: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node]),

  /**
   * **You will not need to use this.** This prop is used by
   * [Tooltip](#tooltip) for improved accessbility.
   *
   * If `true`, the popover will always be rendered *but it will be visually
   * hidden when inactive*.
   *
   * By default, the popover is added to and removed from the DOM dynamically.
   */
  renderHiddenContent: _propTypes.default.bool,

  /**
   * Whether or not the popover receives focus when it opens.
   *
   * This will *only* happen if the trigger is opened with a click.
   */
  receiveFocus: _propTypes.default.bool,

  /**
   * Whether or not the popover receives *and traps* focus when it opens.
   *
   * This will *only* happen if the trigger is opened with a click.
   */
  trapFocus: _propTypes.default.bool,

  /**
   * Whether or not the trigger responds to clicks.
   *
   * **Warning**: You probably don't want to use `respondsToClick` and
   * `receivesFocus` *and* `respondsToFocus` all together, because they can
   * have clashing interactions. For example, when the popover that trapped
   * focus closes, it returns focus to the trigger, which then causes the
   * popover to open again.
   */
  respondsToClick: _propTypes.default.bool,

  /**
   * Whether or not the trigger responds to `mouseenter` and `mouseleave` events.
   */
  respondsToHover: _propTypes.default.bool,

  /**
   * Whether or not the trigger responds to `focus` and `blur` events.
   */
  respondsToFocus: _propTypes.default.bool,

  /**
   * Whether or not the trigger is disabled.
   */
  disabled: _propTypes.default.bool,

  /**
   * Props to pass directly to the [Popover](#popover) component. See Popover's
   * documentation for details.
   */
  popoverProps: _propTypes.default.object,

  /**
   * Props to pass directly to the `<div>` that will wrap your trigger content.
   */
  passthroughTriggerProps: _propTypes.default.object,

  /**
   * Callback that is invoked when the popover opens.
   */
  onPopoverOpen: _propTypes.default.func,

  /**
   * Callback that is invoked when the popover closes.
   */
  onPopoverClose: _propTypes.default.func
};
PopoverTrigger.defaultProps = {
  block: false,
  disabled: false,
  renderHiddenContent: false,
  receiveFocus: true,
  trapFocus: false,
  respondsToClick: true,
  respondsToHover: false,
  respondsToFocus: false,
  popoverProps: {}
};